import { useCallback, useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { CSVLink } from "react-csv";
import { Skeleton } from "primereact/skeleton";
import ReportService from "../../../services/ReportService";
import AuthContext from "../../../auth/AuthContext";
import AlertService from "../../../services/AlertService";

const IncidentReport = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  // Permissions check
  const supportAccess = authContext.getPermissions("report");
  useEffect(() => {
    if (
      supportAccess.filter((f) => f.access.includes(1)).length === 0 ||
      authContext.onGoingValidations
    ) {
      navigate("/");
    }
  }, [supportAccess, navigate, authContext]);

  // Load results
const loadResults = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await ReportService.getIncidentUserReport(
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setResults(data.data || []);
        setCurrentPage(0);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [authContext]);

 useEffect(() => {
    const controller = new AbortController();
    loadResults(controller.signal);
    return () => controller.abort();
  }, [loadResults]);


  const headers = [
    { label: "S.No", key: "serialNo" },
    { label: "Incident Name", key: "incidentName" },
    { label: "Time Spent", key: "timeSpentDesc" },
    { label: "Status", key: "statusDesc" },
  ];

  // Sorting
  const sortedResults = useMemo(() => {
    let sorted = [...results];
    if (sortField) {
      sorted.sort((a, b) => {
        const valA = a[sortField];
        const valB = b[sortField];
        if (valA == null) return -1;
        if (valB == null) return 1;
        return (valA < valB ? -1 : valA > valB ? 1 : 0) * (sortOrder === 1 ? 1 : -1);
      });
    }
    return sorted;
  }, [results, sortField, sortOrder]);


  const paginatedData = useMemo(() => {
    const startIndex = currentPage * rowsPerPage;
    return sortedResults.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedResults, currentPage, rowsPerPage]);


  const csvData = useMemo(() => {
    return sortedResults.map((row, index) => ({
      serialNo: index + 1,
      incidentName: row.incidentName,
      timeSpentDesc: row.timeSpentDesc,
      statusDesc: row.statusDesc,
    }));
  }, [sortedResults]);

  // Pagination numbers generator
  const generatePageNumbers = () => {
    const totalPages = Math.ceil(sortedResults.length / rowsPerPage);
    const currentPageNumber = currentPage + 1;
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPageNumber > maxVisiblePages + 1) pages.push("...");
      let start = Math.max(2, currentPageNumber - 1);
      let end = Math.min(totalPages - 1, currentPageNumber + 1);
      if (currentPageNumber <= maxVisiblePages) end = 4;
      else if (currentPageNumber > totalPages - maxVisiblePages) start = totalPages - 3;
      for (let i = start; i <= end; i++) {
        if (i > 1 && i < totalPages) pages.push(i);
      }
      if (currentPageNumber < totalPages - maxVisiblePages) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const skeletonData = Array.from({ length: rowsPerPage }, (_, i) => ({ id: `skeleton-${i}` }));
  const skeletonBody = () => <Skeleton width="90%" height="1.5rem" />;

  return (
    <>
      <div className="container-fluid custom-container position-relative mb-5">
        <div className="d-flex flex-column flex-md-row justify-content-end align-items-start align-items-md-center mb-3">
          <div className="d-flex flex-wrap gap-4">

            <CSVLink
              data={csvData}
              headers={headers}
              filename={"Incident-User-Report.csv"}
              className="btn btn-primary"
            >
              <i className="pi pi-upload me-2"></i>Export
            </CSVLink>
          </div>
        </div>

        <DataTable
          value={loading ? skeletonData : paginatedData}
          emptyMessage={loading ? "" : "No data found"}
          dataKey="id"
          className="custom-table"
          responsiveLayout="scroll"
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={(e) => {
            setSortField(e.sortField);
            setSortOrder(e.sortOrder);
          }}

                  pt={{
    root: { style: { cursor: "default" } },
    bodyRow: { style: { cursor: "default" } },
    checkbox: { style: { cursor: "default" } }
  }}
        >
          <Column
            header="S. No."
            style={{ width: "80px", textAlign: "center" }}
            bodyClassName="text-center"
            headerClassName="text-center"
            body={loading ? skeletonBody : (_rowData, { rowIndex }) => currentPage * rowsPerPage + rowIndex + 1}
            sortable={false}
          />
          <Column
            field="incidentName"
            header="Incident Name"
            body={loading ? skeletonBody : (rowData) => rowData.incidentName}
            sortable
          />
          <Column
            field="timeSpentDesc"
            header="Time Spent"
            body={loading ? skeletonBody : (rowData) => rowData.timeSpentDesc}

          />
          <Column
            field="statusDesc"
            header="Status"
            body={loading ? skeletonBody : (rowData) => rowData.statusDesc}

          />
        </DataTable>

        {/* Pagination */}
        <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-center mt-3 gap-2 paginator-wrapper">
          <div className="d-flex align-items-center">
            <label className="me-2 fw-bold">Show Rows:</label>
            {loading ? (
              <Skeleton width="100px" height="38px" />
            ) : (
              <select
                className="form-select form-select-sm w-auto py-2 text-secondary custom-input-color"
                value={rowsPerPage}
                onChange={e => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setCurrentPage(0);
                }}
              >
                {[5, 10, 20].map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            )}
          </div>
          {loading ? (
            <div className="d-flex gap-2">
              {Array.from({ length: 5 }).map((_, idx) => (
                <Skeleton key={idx} width="40px" height="40px" />
              ))}
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center gap-2">
              <button
                className="btn p-0 fs-5 pagination-item"
                style={{ width: "40px", height: "40px" }}
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
              >
                &lt;
              </button>
              {generatePageNumbers().map((page, index) =>
                page === "..." ? (
                  <span key={index} className="mx-1 text-secondary">
                    ...
                  </span>
                ) : (
                  <button
                    key={index}
                    className={`btn p-0 pagination-item ${currentPage + 1 === page ? "active-page" : ""}`}
                    style={{ width: "40px", height: "40px" }}
                    onClick={() => setCurrentPage(page - 1)}
                  >
                    {page}
                  </button>
                )
              )}
              <button
                className="btn p-0 fs-5 pagination-item"
                style={{ width: "40px", height: "40px" }}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(Math.ceil(sortedResults.length / rowsPerPage) - 1, prev + 1))
                }
                disabled={currentPage >= Math.ceil(sortedResults.length / rowsPerPage) - 1}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default IncidentReport;
